import React, { memo, useCallback, useMemo } from 'react';
import { IoClose } from 'react-icons/io5';
import { useSelector } from 'react-redux';

import EditableCell from 'components/common/EditableCell';
import { TASKS_GRID_CLEAR_TIMING_ARIA_LABEL } from 'constants/uiConstants';
import { IInputTask, IInputTaskBase } from 'interfaces/IInputTask';

import { selectActiveGOrgId } from '../../../../redux/selectors/GlobalCatalogueSelectors';

import QuickAddConfirm from './QuickAddConfirm';
import { useTimingCatalogue } from './useTimingCatalogue';

export interface TimingValue {
  item: string;
  globalId: string;
}

interface TimingCellProps {
  task: IInputTask;
  commitTaskField: (task: IInputTask, patch: Partial<IInputTaskBase>) => void;
  /** The field's classes; the task row's rounded cell by default. */
  className?: string;
}

const TimingCell: React.FC<TimingCellProps> = ({
  task,
  commitTaskField,
  className = 'h-full w-full rounded',
}) => {
  const gOrgId = useSelector(selectActiveGOrgId);
  const value = task.timing?.globalId ?? null;
  const displayValue = task.timing?.item ?? '';

  const {
    items: timingItems,
    isLoading,
    isPaginating,
    hasNextPage,
    fetchNextPage,
    searchTerm,
    onSearch,
  } = useTimingCatalogue(gOrgId);

  const timingOptions = useMemo(
    () => timingItems.map((t) => ({ value: t.globalId, label: t.name })),
    [timingItems]
  );

  const onEditStart = useCallback(
    () => onSearch(displayValue),
    [onSearch, displayValue]
  );

  const onCommit = useCallback(
    (timing: TimingValue | null) => commitTaskField(task, { timing }),
    [commitTaskField, task]
  );

  return (
    <div className="group/timing relative h-full w-full">
      <EditableCell
        type="select"
        className={className}
        value={value}
        displayValue={displayValue || ' '}
        options={timingOptions}
        placeholder="Select timing"
        allowClear
        // Timings can be long ("GS 14-4 leaves …"); the list shows them whole.
        popupMatchSelectWidth={false}
        remoteSearch={{
          searchValue: searchTerm,
          onSearch,
          loading: isLoading,
          isPaginating,
          hasNextPage: !!hasNextPage,
          onLoadMore: fetchNextPage,
          // Nothing matches what was typed: offer to add it (OP-2498).
          emptyAction: searchTerm.trim() ? (
            <QuickAddConfirm
              kind="timing"
              searchText={searchTerm}
              onCreated={(record) =>
                onCommit({ item: record.name, globalId: record.globalId })
              }
            />
          ) : undefined,
        }}
        onEditStart={onEditStart}
        onCommit={(globalId) => {
          if (globalId === null) {
            onCommit(null);
            return;
          }
          const selected = timingItems.find((t) => t.globalId === globalId);
          if (!selected) return;
          onCommit({ item: selected.name, globalId: selected.globalId });
        }}
      />
      {/* One-click clear (AC 22). Opening the cell seeds its search with the
          current timing, so clearing from inside the editor takes two
          clicks; this clears the value directly. */}
      {value && (
        <button
          type="button"
          aria-label={TASKS_GRID_CLEAR_TIMING_ARIA_LABEL}
          className="absolute right-6 top-1/2 -translate-y-1/2 rounded bg-white p-0.5 text-gray-dark opacity-0 hover:bg-gray-bright focus-visible:opacity-100 group-hover/timing:opacity-100 group-has-[.ant-select]/timing:hidden"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onCommit(null);
          }}
        >
          <IoClose size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default memo(TimingCell);
